import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import { TbReceipt } from 'react-icons/tb';

import {
  ApolloError,
  Box,
  HStack,
  Icon,
  IconButton,
  LoadingIndicator,
  useShowMessage,
} from '../../../core-ui';
import { useShowApolloError } from '../../../support/graphql/useShowApolloError';
import { useExport } from '../../../support/useExport';
import { BulkVendorSalesInput } from '../../../types/__generated__/globalTypes';
import { useNavigation } from '../../Navigation';
import { parseInput } from '../../uiold/CurrencyInput';
import { SelectedDateRange } from '../VendorBillingExportDialog';
import { ExportSalesButton } from './ExportIconButton';
import { ExportVendorSalesModal } from './ExportVendorSalesModal';
import { BULK_UPDATE_VENDOR_SALES, GET_FEES_AND_SALES } from './graphql';
import { makeRow, Row, Rows } from './helpers';
import { SalesTable } from './SalesTable';
import {
  BulkUpdateVendorSales,
  BulkUpdateVendorSalesVariables,
} from './__generated__/BulkUpdateVendorSales';
import {
  GetFeesAndSales,
  GetFeesAndSalesVariables,
} from './__generated__/GetFeesAndSales';

type Props = {
  selectedMarketId: string;
  selectedMarketDate: string;
  seasonId: string;
};

function getPlacedVendors(queryData: GetFeesAndSales) {
  const flatPlacements = queryData.season?.flatPlacements ?? [];
  const placedVendorIdsSet = new Set(
    flatPlacements.map((item) => item.vendorId),
  );
  const rawVendors = queryData.season?.vendors ?? [];
  const placedVendors = rawVendors.filter((vendor) =>
    placedVendorIdsSet.has(vendor.id),
  );
  return placedVendors;
}

const saveVendorSales = async (input: {
  onSaveUpdate: (updates: Array<BulkVendorSalesInput>) => void;
  queryData: GetFeesAndSales;
  rows: Rows;
  selectedMarketDate: string;
  selectedMarketId: string;
}) => {
  const {
    onSaveUpdate,
    queryData,
    rows,
    selectedMarketDate,
    selectedMarketId,
  } = input;
  const placedVendors = getPlacedVendors(queryData);
  let updates: Array<BulkVendorSalesInput> = [];
  for (const vendor of placedVendors) {
    const row = rows.find((item) => item?.vendorId === vendor.id);
    if (!row) {
      continue;
    }
    const flatFeesByIdList = Object.entries(row.flatFees).map(
      ([key, value]) => {
        return { itemId: key, amountInCents: Number(value) };
      },
    );
    const redemptionsByIdList = Object.entries(row.redemptions).map(
      ([key, value]) => {
        return {
          itemId: key,
          amountInCents: Math.round(parseInput(String(value))),
        };
      },
    );
    const update = {
      id: row.salesId,
      date: selectedMarketDate,
      flatFeesByIdList,
      redemptionsByIdList,
      salesAmountInCents: parseInput(String(row.sales)),
      percentage: Number(row.percentage),
      vendorId: row.vendorId,
      marketId: selectedMarketId,
    };
    updates.push(update);
  }
  onSaveUpdate(updates);
};

export function Sales(props: Props) {
  const { seasonId, selectedMarketId, selectedMarketDate } = props;
  const showMessage = useShowMessage();
  let [doExport, { isLoading: isExportLoading }] = useExport(
    '/api/vendor-sales/export',
    {
      onSuccess: () => {
        showMessage('success', 'Vendor sales data exported.');
      },
    },
  );
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [dateRange, setDateRange] = useState<SelectedDateRange>([]);
  const { navigate } = useNavigation();
  const [rows, setRows] = useState<Rows | null>(null);
  const onCompleted = (data: GetFeesAndSales) => {
    const placedVendors = getPlacedVendors(data);
    const initialValue: Rows = [];
    const initialRows = placedVendors.reduce((prev, vendor) => {
      const newRow = makeRow({ ...data, vendorId: vendor.id });
      if (!newRow) {
        return prev;
      }
      return [...prev, newRow];
    }, initialValue);
    setRows(initialRows);
  };
  const showApolloError = useShowApolloError();
  const { data: queryData, error, refetch } = useQuery<
    GetFeesAndSales,
    GetFeesAndSalesVariables
  >(GET_FEES_AND_SALES, {
    variables: {
      marketId: selectedMarketId,
      date: selectedMarketDate,
      seasonId,
    },
    onCompleted,
  });
  const [bulkUpdateVendorSales] = useMutation<
    BulkUpdateVendorSales,
    BulkUpdateVendorSalesVariables
  >(BULK_UPDATE_VENDOR_SALES, {
    onError: showApolloError,
    onCompleted: async () => {
      const { data: refetchData } = await refetch();
      onCompleted(refetchData);
    },
  });

  if (error) {
    return <ApolloError error={error} />;
  }
  if (!queryData) {
    return <LoadingIndicator />;
  }

  const onSaveUpdate = async (updates: Array<BulkVendorSalesInput>) => {
    await bulkUpdateVendorSales({
      variables: {
        updates,
      },
    });
    showMessage('success', t('Sales updated successfully'), 'top', 300);
  };

  async function saveVendorSalesRow(row: Row) {
    if (!queryData) {
      return;
    }
    await saveVendorSales({
      onSaveUpdate,
      queryData,
      rows: [row],
      selectedMarketDate,
      selectedMarketId,
    });
  }

  return (
    <Box maxHeight="100%">
      {rows == null ? (
        <LoadingIndicator />
      ) : (
        <>
          <HStack>
            <IconButton
              aria-label="Full Screen"
              icon={
                <Icon
                  as={TbReceipt}
                  fontSize={20}
                  onClick={() => navigate('/billing/new-invoice')}
                />
              }
              size="md"
            />
            <ExportSalesButton
              isLoading={isExportLoading}
              onClick={() => {
                setShowExportDialog(true);
              }}
            />
          </HStack>
          <SalesTable
            queryData={queryData}
            rows={rows}
            saveVendorSalesRow={saveVendorSalesRow}
            setRows={setRows}
          />
        </>
      )}
      {showExportDialog ? (
        <ExportVendorSalesModal
          dateRange={dateRange}
          isLoading={isExportLoading}
          onSubmit={() => {
            if (dateRange.length !== 2) {
              showMessage(
                'warning',
                t('Please select two dates to create a date range.'),
              );
              return;
            }
            doExport({
              currency: queryData.myAccount.settings.currency,
              dateRange:
                dateRange.length === 2 ? dateRange : [dateRange, dateRange],
            });
          }}
          onDismiss={() => {
            setDateRange([]);
            setShowExportDialog(false);
          }}
          setDateRange={setDateRange}
        />
      ) : null}
    </Box>
  );
}
